import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import './display.less';

export type ExamFont = 'standard' | 'large' | 'extra-large';
export type ExamColour = 'standard' | 'sepia' | 'dark';
interface Preferences {
  font: ExamFont;
  colour: ExamColour;
}

const STORAGE_KEY = 'ielts-mock-display:v1';
const defaults: Preferences = {font: 'standard', colour: 'standard'};
interface DisplayState {
  preferences: Preferences;
  setFont: (font: ExamFont) => void;
  setColour: (colour: ExamColour) => void;
  reset: () => void;
}
const DisplayContext = createContext<DisplayState | null>(null);

function readPreferences(): Preferences {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return {
      font: ['standard', 'large', 'extra-large'].includes(stored?.font) ? stored.font : defaults.font,
      colour: ['standard', 'sepia', 'dark'].includes(stored?.colour) ? stored.colour : defaults.colour,
    };
  } catch {
    return defaults;
  }
}

export const useExamDisplay = () => useContext(DisplayContext);

// Keep display state below the exam controller. Updating it preserves the
// children, audio element, answer state and running submission lifecycle.
export default function ExamDisplay({children, className = '', enabled = true}: PropsWithChildren<{className?: string; enabled?: boolean;}>) {
  const [preferences, setPreferences] = useState(readPreferences);

  useEffect(() => {
    if (!enabled) return;
    try {
      if (preferences.font === defaults.font && preferences.colour === defaults.colour) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
      }
    } catch {
      // Display controls also work when browser storage is unavailable.
    }
  }, [enabled, preferences]);

  const value: DisplayState = {
    preferences,
    setFont: font => setPreferences(current => ({...current, font})),
    setColour: colour => setPreferences(current => ({...current, colour})),
    reset: () => setPreferences(defaults),
  };
  return <DisplayContext.Provider value={enabled ? value : null}>
    <div className={`exam-display ${className}`} data-exam-font={enabled ? preferences.font : defaults.font} data-exam-colour={enabled ? preferences.colour : defaults.colour}>
      {children}
    </div>
  </DisplayContext.Provider>;
}
